/**
 *  @class
 *  @function FacetsToggle
 */
if (!customElements.get('facets-toggle')) {
  class FacetsToggle extends HTMLElement {
    connectedCallback() {
      this.drawer = document.getElementById('Facet-Drawer');

      this.addEventListener('click', (e) => {
        e.preventDefault();
        if (OverlayManager.isOpen(this.drawer)) {
          OverlayManager.close(this.drawer);
        } else {
          OverlayManager.open(this.drawer);
        }
      });

      setTimeout(() => {
        window.dispatchEvent(new Event('resize.resize-select'));
      }, 10);
    }
  }
  customElements.define('facets-toggle', FacetsToggle);
}


class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 500);

    this.querySelector('form').addEventListener('input', this.debouncedOnSubmit.bind(this));
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const container = document.getElementsByClassName('thb-filter-count');
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');

    for (var item of container) {
      item.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = element => element.url === url;

      if (FacetFiltersForm.filterData.some(filterDataUrl)) {
        FacetFiltersForm.renderSectionFromCache(filterDataUrl, event);
      } else {
        FacetFiltersForm.renderSectionFromFetch(url, event);
      }
    });
    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);

    FacetFiltersForm.syncSortSelects(searchParams);
    FacetFiltersForm.publishFacetUpdate(searchParams);
  }

  // The bar and the drawer each own a sort <select>, and neither sits inside a
  // collapsible-row — so renderFilters never refreshes them. Without this, a
  // sort chosen in one surface stays stale in the other, and that other form's
  // next submit silently reverts it. Assigning .value fires no input event, so
  // this can't loop back into onSubmitHandler.
  static syncSortSelects(searchParams) {
    const sortBy = new URLSearchParams(searchParams).get('sort_by');
    if (!sortBy) return;

    document.querySelectorAll('select.facet-filters__sort').forEach((select) => {
      if (select.value === sortBy) return;
      select.value = sortBy;
      // Bubbles so whichever wrapper owns the select can react — sort-dropdown
      // repaints its option list, resize-select re-measures its width. `change`
      // and not `input`: the form submits on `input`, and this is a sync of a
      // change that has already been applied, not a new one.
      select.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  // shopify:collection:update / shopify:search:update — filters or sort changed
  static publishFacetUpdate(searchParams) {
    const SE = window.Shopify && window.Shopify.StandardEvents;
    if (!SE) return;
    const params = new URLSearchParams(searchParams);

    if (theme.settings.collection_id) {
      publishStandardEvent('CollectionUpdateEvent', {
        collection: { id: theme.settings.collection_id },
        filters: SE.CollectionUpdateEvent.parseProductFilters(params),
        sortKey: SE.CollectionUpdateEvent.getSortKey(params)
      });
    } else if (window.location.pathname.indexOf(theme.routes.search_url) !== -1) {
      publishStandardEvent('SearchUpdateEvent', {
        searchTerm: params.get('q') || new URLSearchParams(window.location.search).get('q') || '',
        filters: SE.SearchUpdateEvent.parseProductFilters(params),
        sortKey: SE.SearchUpdateEvent.getSortKey(params)
      });
    }
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then(response => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, {
          html,
          url
        }];
        withViewTransition(() => {
          FacetFiltersForm.renderFilters(html, event);
          FacetFiltersForm.renderProductGridContainer(html);
          FacetFiltersForm.renderProductCount(html);
        });
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    withViewTransition(() => {
      FacetFiltersForm.renderFilters(html, event);
      FacetFiltersForm.renderProductGridContainer(html);
      FacetFiltersForm.renderProductCount(html);
    });
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductGridContainer').innerHTML;

    let top = document.getElementById('ProductGridContainer').getBoundingClientRect().top + document.documentElement.scrollTop - parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-height'), 10) - 30;

    window.scrollTo({
      top: top,
      left: 0,
      behavior: "smooth"
    });
  }

  static renderProductCount(html) {
    const countHtml = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount');
    const container = document.getElementsByClassName('thb-filter-count');

    if (countHtml) {
      for (var item of container) {
        item.innerHTML = countHtml.innerHTML;
        item.classList.remove('loading');
      }
    }

  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');

    const facetDetailsElements =
      parsedHTML.querySelectorAll('#FacetFiltersForm collapsible-row, #FacetFiltersFormMobile collapsible-row, #FacetFiltersForm-bar collapsible-row');
    const matchesIndex = (element) => {
      const jsFilter = event ? event.target.closest('collapsible-row') : undefined;
      return jsFilter ? element.dataset.index === jsFilter.dataset.index : false;
    };
    const facetsToRender = Array.from(facetDetailsElements).filter(element => !matchesIndex(element));
    const countsToRender = Array.from(facetDetailsElements).find(matchesIndex);

    facetsToRender.forEach((element) => {
      const target = document.querySelector(`collapsible-row[data-index="${element.dataset.index}"]`);
      if (target) target.innerHTML = element.innerHTML;
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) FacetFiltersForm.renderCounts(countsToRender, event.target.closest('collapsible-row'));
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    // .facets__preserved-mobile holds the drawer's hidden inputs for filters
    // hidden on this collection. It lives outside every collapsible-row, so
    // without this it would keep submitting a filter the customer has removed.
    const mobileElementSelectors = ['.mobile-filter-count', '.facets__preserved-mobile'];

    mobileElementSelectors.forEach((selector) => {
      const source = html.querySelector(selector);
      const target = document.querySelector(selector);
      if (!source || !target) return;
      target.innerHTML = source.innerHTML;
    });

  }

  static renderCounts(source, target) {
    const targetElement = target.querySelector('.facets__selected');
    const sourceElement = source.querySelector('.facets__selected');

    if (sourceElement && targetElement) {
      target.querySelector('.facets__selected').outerHTML = source.querySelector('.facets__selected').outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({
      searchParams
    }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      }];
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const formData = new FormData(event.target.closest('form'));
    const searchParams = new URLSearchParams(formData);
    if (searchParams.get('filter.v.price.gte') === "0.00") {
      searchParams.delete('filter.v.price.gte');
    }
    if (document.querySelector('.price_slider')) {
      if (searchParams.get('filter.v.price.lte') === parseFloat(document.querySelector('.price_slider').dataset.max).toFixed(2)) {
        searchParams.delete('filter.v.price.lte');
      }
    }
    FacetFiltersForm.renderPage(searchParams.toString(), event);
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url = event.currentTarget.href.indexOf('?') == -1 ? '' : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();


class FacetRemove extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('a').forEach((item) => {
      item.addEventListener('click', (event) => {
        event.preventDefault();
        const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
        form.onActiveFilterClick(event);
      });
    });
  }
}

customElements.define('facet-remove', FacetRemove);

/**
 *  @class
 *  @function SortDropdown
 *
 *  Presentation layer over the filter bar's native sort <select>.
 *
 *  The <select> is never replaced — it stays in the form as the single source
 *  of truth, so FormData still serialises sort_by and syncSortSelects can still
 *  write to it from another surface. This only hides it once the script has
 *  actually run and drives a button plus a listbox in its place; if the script
 *  fails to load the customer is left with a working native control.
 *
 *  Choosing an option dispatches `input` on the select, which is exactly what a
 *  real interaction with it would have bubbled up to <facet-filters-form>.
 */
if (!customElements.get('sort-dropdown')) {
  class SortDropdown extends HTMLElement {
    connectedCallback() {
      this.select = this.querySelector('select');
      this.toggle = this.querySelector('.sort-dropdown__toggle');
      this.list = this.querySelector('.sort-dropdown__list');
      if (!this.select || !this.toggle || !this.list) return;

      this.options = Array.from(this.list.querySelectorAll('[role="option"]'));
      this.labelEl = this.querySelector('.sort-dropdown__label');
      this.valueEl = this.querySelector('.sort-dropdown__value');

      this.onDocumentClick = this.onDocumentClick.bind(this);

      this.toggle.addEventListener('click', () => this.setOpen(!this.isOpen));
      this.list.addEventListener('click', (event) => {
        const option = event.target.closest('[role="option"]');
        if (option) this.choose(option);
      });
      this.addEventListener('keydown', (event) => this.onKeydown(event));
      this.select.addEventListener('change', () => this.syncFromSelect());

      this.syncFromSelect();

      // Last, so any throw above leaves the native select visible.
      this.toggle.hidden = false;
      this.classList.add('sort-dropdown--ready');
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocumentClick);
    }

    get isOpen() {
      return this.toggle.getAttribute('aria-expanded') === 'true';
    }

    setOpen(open) {
      this.toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      this.list.hidden = !open;
      this.classList.toggle('is-open', open);

      if (!open) {
        document.removeEventListener('click', this.onDocumentClick);
        return;
      }
      document.addEventListener('click', this.onDocumentClick);
      this.focusOption(this.options.find((option) => option.getAttribute('aria-selected') === 'true') || this.options[0]);
    }

    onDocumentClick(event) {
      if (this.contains(event.target)) return;
      this.setOpen(false);
    }

    focusOption(option) {
      if (!option) return;
      this.options.forEach((item) => { item.tabIndex = item === option ? 0 : -1; });
      option.focus();
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        if (!this.isOpen) return;
        // The cart notification and the overlay stack both watch Escape at
        // document level — closing this dropdown must not also dismiss those.
        event.stopPropagation();
        this.setOpen(false);
        this.toggle.focus();
        return;
      }

      if (event.key === 'Tab') {
        if (this.isOpen) this.setOpen(false);
        return;
      }

      const inList = this.list.contains(event.target);
      if (!inList && event.target !== this.toggle) return;

      if (!this.isOpen) {
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
          event.preventDefault();
          this.setOpen(true);
        }
        return;
      }
      if (!inList) return;

      const last = this.options.length - 1;
      const index = this.options.indexOf(event.target);
      let next = null;

      if (event.key === 'ArrowDown') next = this.options[index + 1] || this.options[0];
      else if (event.key === 'ArrowUp') next = index > 0 ? this.options[index - 1] : this.options[last];
      else if (event.key === 'Home') next = this.options[0];
      else if (event.key === 'End') next = this.options[last];

      if (!next) return;
      event.preventDefault();
      this.focusOption(next);
    }

    choose(option) {
      const value = option.dataset.value;
      this.setOpen(false);
      this.toggle.focus();

      if (this.select.value === value) return;
      this.select.value = value;
      this.syncFromSelect();
      this.select.dispatchEvent(new Event('input', { bubbles: true }));
    }

    syncFromSelect() {
      const value = this.select.value;
      this.options.forEach((option) => {
        const selected = option.dataset.value === value;
        option.setAttribute('aria-selected', selected ? 'true' : 'false');
        option.classList.toggle('is-selected', selected);
        option.tabIndex = selected ? 0 : -1;
      });

      const selectedOption = this.select.selectedOptions[0];
      if (this.valueEl) this.valueEl.textContent = selectedOption ? selectedOption.textContent : '';

      // Swap the toggle between "Sort by" and the applied order's own name,
      // mirroring what the section would render for this sort on a fresh load.
      // Whichever is not on screen stays visually-hidden rather than removed,
      // so the button keeps announcing both.
      const isDefault = value === this.dataset.defaultSort;
      if (this.labelEl) this.labelEl.classList.toggle('visually-hidden', !isDefault);
      if (this.valueEl) this.valueEl.classList.toggle('visually-hidden', isDefault);
    }
  }
  customElements.define('sort-dropdown', SortDropdown);
}

/**
 *  @class
 *  @function PriceSlider
 */
class PriceSlider extends HTMLElement {

  constructor() {
    super();
  }
  connectedCallback() {
    let slider = this.querySelector('.price_slider'),
      amounts = this.querySelector('.price_slider_amount'),
      args = {
        start: [parseFloat(slider.dataset.minValue || 0), parseFloat(slider.dataset.maxValue || slider.dataset.max)],
        connect: true,
        step: 10,
        direction: document.dir,
        handleAttributes: [
          { 'aria-label': 'lower' },
          { 'aria-label': 'upper' },
        ],
        range: {
          'min': 0,
          'max': parseFloat(slider.dataset.max)
        }
      },
      event = new CustomEvent('input'),
      form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');

    if (slider.classList.contains('noUi-target')) {
      slider.noUiSlider.destroy();
    }
    noUiSlider.create(slider, args);

    const minInput = amounts.querySelector('.field__input_min'),
      maxInput = amounts.querySelector('.field__input_max');

    // Slider → fields. A field being typed in is left alone, so moving the handle from the field
    // (below) never rewrites what the customer is typing.
    slider.noUiSlider.on('update', function (values) {
      if (document.activeElement !== minInput) minInput.value = values[0];
      if (document.activeElement !== maxInput) maxInput.value = values[1];
    });
    slider.noUiSlider.on('change', function (values) {
      form.querySelector('form').dispatchEvent(event);
    });

    // Fields → slider. Typing a price only filtered the products; the handles never moved.
    const toNumber = (value, fallback) => {
      const number = parseFloat(String(value).replace(/[^\d.]/g, ''));
      return isNaN(number) ? fallback : number;
    };
    const rangeMax = parseFloat(slider.dataset.max);
    const syncFromFields = () => {
      slider.noUiSlider.set([toNumber(minInput.value, 0), toNumber(maxInput.value, rangeMax)]);
    };
    [minInput, maxInput].forEach((input) => {
      // Numeric keypad on phones.
      input.setAttribute('inputmode', 'decimal');
      input.addEventListener('input', syncFromFields);
      // Once editing ends (blur, Enter, the keypad's Done), show the slider's value in the field.
      // If the slider had to correct it (out of range, min above max), filter by the corrected price.
      input.addEventListener('change', () => {
        const values = slider.noUiSlider.get();
        const typed = input === minInput ? toNumber(input.value, 0) : toNumber(input.value, rangeMax);
        const actual = parseFloat(input === minInput ? values[0] : values[1]);
        minInput.value = values[0];
        maxInput.value = values[1];
        if (typed !== actual) form.querySelector('form').dispatchEvent(new CustomEvent('input'));
      });
    });
  }
}
customElements.define('price-slider', PriceSlider);

(function () {
  const updateShift = (label) => {
    const content = label.querySelector('.facet-tooltip__content');
    if (!content) return;
    label.style.setProperty('--tooltip-shift', '0px');
    const rect = content.getBoundingClientRect();
    const gutter = 8;
    let min = gutter;
    let max = document.documentElement.clientWidth - gutter;
    // A scrolling list (the horizontal bar's dropdown) clips anything past its own edges, so keep
    // the tooltip inside that list as well as inside the viewport.
    const list = label.closest('ul');
    if (list && getComputedStyle(list).overflowX !== 'visible') {
      const box = list.getBoundingClientRect();
      min = Math.max(min, box.left + 2);
      max = Math.min(max, box.right - 2);
    }
    let shift = 0;
    if (rect.left < min) {
      shift = Math.round(min - rect.left);
    } else if (rect.right > max) {
      shift = Math.round(max - rect.right);
    }
    label.style.setProperty('--tooltip-shift', shift + 'px');
  };

  document.addEventListener('mouseover', (e) => {
    if (!e.target.closest) return;
    const label = e.target.closest('.facet-checkbox--tooltip');
    if (!label || label._tooltipHovered) return;
    label._tooltipHovered = true;
    updateShift(label);
  });

  document.addEventListener('mouseout', (e) => {
    if (!e.target.closest) return;
    const label = e.target.closest('.facet-checkbox--tooltip');
    if (!label) return;
    if (!label.contains(e.relatedTarget)) {
      label._tooltipHovered = false;
    }
  });
})();
